import { css, cva } from '@repo/panda/css';
import type { ReactNode } from 'react';
import { Toaster as HotToaster, toast as hotToast, type ToastPosition } from 'react-hot-toast';
import { IconButton } from '../ds/button';
import { Icon } from '../ds/icon';
import type { MaterialSymbolName } from '../types/material-symbol-names';
import { Type } from './type';

export type ToastVariant = 'info' | 'success' | 'error' | 'warning';

export type ToastOptions = {
    title?: ReactNode;
    description?: ReactNode;
    icon?: ReactNode;
    duration?: number;
    variant?: ToastVariant;
    position?: ToastPosition;
};

const toastStyles = cva({
    base: {
        backgroundColor: 'grey.900',
        borderRadius: '8',
        boxShadow: 'lg',
        minWidth: 'xs',
        maxWidth: 'sm',
        opacity: 1,
        overflowWrap: 'anywhere',
        p: '12',
        position: 'relative',
        pointerEvents: 'auto',
        display: 'flex',
        flexDirection: 'column',
        gap: '2',
        transition: 'all',
        willChange: 'transform, opacity, scale',
        zIndex: 2000,
    },
    variants: {
        variant: {
            info: {
                backgroundColor: 'cyan.600',
                color: 'cyan.400',
            },
            success: {
                backgroundColor: 'limeGreen.600',
                color: 'limeGreen.400',
            },
            error: {
                backgroundColor: 'red.800',
                color: 'red.400',
            },
            warning: {
                backgroundColor: 'orange.800',
                color: 'orange.400',
            },
        },
    },
});

const defaultIcons: Record<ToastVariant, MaterialSymbolName> = {
    info: 'info',
    success: 'check_circle',
    error: 'error',
    warning: 'warning',
};

export const toast = (options: ToastOptions | string) => {
    if (typeof options === 'string') {
        return hotToast(options);
    }
    const { title, description, icon, duration, variant, position, ...rest } = options;

    const displayIcon =
        icon ||
        (variant ? (
            <Icon className={css({ flexShrink: 0 })} fill icon={defaultIcons[variant]} size="md" decorative />
        ) : null);

    return hotToast.custom(
        t => (
            // `data-toast` lets an open modal ignore clicks on the toast, as it does for ds toasts.
            <div data-toast="" className={toastStyles({ variant: variant || undefined })}>
                <div className={css({ display: 'flex', alignItems: 'flex-start', gap: '6' })}>
                    {displayIcon}
                    <div className={css({ flex: '1', minWidth: '0', paddingRight: '32' })}>
                        {title && (
                            <Type variant="header-s" color="text.primary" className={css({ wordBreak: 'break-word' })}>
                                {title}
                            </Type>
                        )}
                        {description && (
                            <Type variant="body-m" color="text.primary" className={css({ wordBreak: 'break-word' })}>
                                {description}
                            </Type>
                        )}
                    </div>
                </div>
                <IconButton
                    variant="ghost"
                    onClick={() => hotToast.remove(t.id)}
                    color="primary"
                    size="sm"
                    aria-label="Close"
                    className={css({
                        position: 'absolute',
                        top: '[50%]',
                        right: '24',
                        transform: 'translate(50%, -50%)',
                    })}
                >
                    <Icon icon="close" decorative />
                </IconButton>
            </div>
        ),
        { duration, position, ...rest },
    );
};

export const ToasterProvider = ({ className }: { className?: string }) => (
    <HotToaster
        position="bottom-right"
        containerClassName={`${css({
            // Let react-hot-toast position toasts; do not override with fixed offsets.
            pointerEvents: 'none',
        })}${className ? ` ${className}` : ''}`}
        toastOptions={{
            className: css({ background: 'transparent', p: '0' }),
            duration: 3000,
        }}
    />
);
